import React from 'react';
import { classNames } from '../../utils/formatters';

const VARIANTS = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700 border-transparent shadow-sm',
  secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-transparent',
  danger: 'bg-red-600 text-white hover:bg-red-700 border-transparent shadow-sm',
  approve: 'bg-green-600 text-white hover:bg-green-700 border-transparent shadow-sm',
  ghost: 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-300',
};

const Spinner = () => (
  <svg
    className="animate-spin h-4 w-4"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

/**
 * Variant-driven button used for modal footers and actions.
 *
 * Props:
 *  - variant: 'primary' | 'secondary' | 'danger' | 'approve' | 'ghost'
 *  - loading: shows a spinner, disables interaction, sets aria-busy
 *  - fullWidth: stretches to fill its container
 */
const Button = React.forwardRef(function Button(
  {
    variant = 'primary',
    loading = false,
    fullWidth = false,
    disabled = false,
    type = 'button',
    className,
    children,
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={classNames(
        'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg',
        'text-sm font-semibold transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
        'disabled:opacity-60 disabled:cursor-not-allowed',
        VARIANTS[variant] || VARIANTS.primary,
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
});

export default Button;
